import { useEffect, useState } from 'react'
import { pontosMontagem, validarLigacao, type LigacaoEstabilizacao } from '../dominio/didatico/montagemEstabilizacao'
import type { ColocacaoEquipamento, EquipamentoEstabilizacao, PontoSolo, PosicaoEstabilizacao } from '../dominio/didatico/estabilizacao'
import type { PerfilVeiculo } from '../dominio/didatico/perfilVeiculo'

export interface PropsMontagem {
  ativo: boolean
  posicao: PosicaoEstabilizacao
  perfil: PerfilVeiculo
  ferramenta: EquipamentoEstabilizacao | 'selecao'
  colocacoes: readonly ColocacaoEquipamento[]
  selecionado: string | null
  aoSelecionar: (id: string | null) => void
  aoPosicionar: (e: EquipamentoEstabilizacao, p: PontoSolo, a?: Partial<ColocacaoEquipamento>) => void
  aoAtualizar: (id: string, a: Partial<ColocacaoEquipamento>) => void
}

export function useMontagemEstabilizacao(p: PropsMontagem) {
  const [rascunho, setRascunho] = useState<LigacaoEstabilizacao>({ tensionada: false, retorno: true })
  const [campoEdicao, setCampoEdicao] = useState<'origem' | 'destino' | null>(null)
  useEffect(() => { setRascunho({ tensionada: false, retorno: true }); setCampoEdicao(null) }, [p.ativo, p.posicao, p.perfil.id, p.ferramenta, p.selecionado])
  const selecionado = p.colocacoes.find(c => c.id === p.selecionado)
  const item = selecionado?.equipamento === 'fitaCatraca' ? selecionado : undefined
  const tipo = selecionado?.equipamento ?? p.ferramenta
  const ligacao = item?.ligacao ?? rascunho
  const pontos = pontosMontagem(p.posicao, p.perfil)
  const visivel = p.ativo && tipo === 'fitaCatraca'
  const campo = !visivel ? null : campoEdicao ?? (!ligacao.origem ? 'origem' : !ligacao.destino ? 'destino' : null)
  const origem = pontos.find(q => q.id === ligacao.origem)
  const disponiveis = !visivel || !campo ? [] : pontos.filter(q => {
    if (q.uso !== 'ancora') return false
    if (campo === 'origem') return q.entidade === 'veiculo' || q.entidade === 'inferior'
    return q.entidade !== origem?.entidade && (origem?.entidade === 'veiculo' || q.entidade === 'veiculo')
  })
  const atualizar = (l: LigacaoEstabilizacao) => item ? p.aoAtualizar(item.id, { ligacao: l }) : setRascunho(l)
  const escolher = (id: string) => {
    if (!campo || !disponiveis.some(q => q.id === id)) return
    const l = { ...ligacao, [campo]: id, tensionada: false }
    if (campo === 'origem') l.destino = undefined
    atualizar(l)
    setCampoEdicao(null)
  }
  const candidato: ColocacaoEquipamento = item ? { ...item, ligacao: { ...ligacao, tensionada: true } }
    : { id: 'rascunho', equipamento: 'fitaCatraca', posicao: { x: origem?.ponto[0] ?? 0, z: origem?.ponto[2] ?? 0 }, ligacao: { ...ligacao, tensionada: true } }
  const conferido = validarLigacao(candidato, pontos)
  const validacao = { ...conferido, mensagem: conferido.completa && !ligacao.tensionada ? 'Geometria montada: falta tensionar a fita.' : conferido.mensagem }
  const tensionar = () => {
    if (item && ligacao.tensionada) { atualizar({ ...ligacao, tensionada: false }); return }
    if (!validacao.completa) return
    if (item) atualizar({ ...ligacao, tensionada: !ligacao.tensionada })
    else p.aoPosicionar('fitaCatraca', candidato.posicao, { ligacao: candidato.ligacao })
  }
  const editar = (campo: 'origem' | 'destino') => { atualizar({ ...ligacao, tensionada: false }); setCampoEdicao(campo) }
  const cancelar = () => { setRascunho({ tensionada: false, retorno: true }); setCampoEdicao(null); p.aoSelecionar(null) }
  const instrucao = campo === 'origem' ? '1. Escolha o primeiro ponto no veículo.' : campo === 'destino' ? '2. Escolha o segundo veículo, poste ou árvore.' : '3. Confira o percurso e tensione a catraca.'
  return { visivel, tipo, item, ligacao, pontos, disponiveis, campo, validacao, instrucao, escolher, tensionar, editar, cancelar,
    previa: visivel && tipo === 'fitaCatraca' && !item && ligacao.origem && ligacao.destino ? { ...candidato, ligacao } : undefined,
    alterarRetorno: (retorno: boolean) => atualizar({ ...ligacao, retorno, tensionada: false }) }
}
export type ControleMontagem = ReturnType<typeof useMontagemEstabilizacao>
